import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch } from "react-redux";
import { signinUser } from "../../services/auth.services";
import { addUser } from "../../store/userSlice";
import { showSnackBar } from "../../store/common/snackBarSlice";
import InputField from "../../Components/Common/Form/InputField";
import logoLight from "../../Components/logo.png";
import logoDark from "../../Components/logo-dark.png";
import { useThemeMode } from "../../theme/ThemeModeContext";
import { useTranslation } from "react-i18next";

const EnvelopeIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const LockIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 11c-1.104 0-2-.896-2-2V6c0-1.104.896-2 2-2s2 .896 2 2v3c0 1.104-.896 2-2 2zm3 0h1c1.104 0 2 .896 2 2v7c0 1.104-.896 2-2 2H8c-1.104 0-2-.896-2-2v-7c0-1.104.896-2 2-2h1"
    />
  </svg>
);

const BackIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
  </svg>
);

const BtnLoader = () => (
  <div className="flex justify-center items-center">
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
  </div>
);

const LoginForm = () => {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const { role } = useParams();
  const dispatch = useDispatch();
  const { resolvedTheme } = useThemeMode();
  const Logo = resolvedTheme === "dark" ? logoDark : logoLight;

  const roleMap = {
    shipper: "Shipper",
    carrier: "Carrier",
    warehouse: "warehouseUser",
  };

  const activeRole = role ? roleMap[role.toLowerCase()] : null;
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const recaptchaRef = useRef(null);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      validateSingleField(field, value);
    }
  };

  const validateSingleField = (field, value) => {
    let isValid = false;
    switch (field) {
      case "email":
        isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        break;
      case "password":
        isValid = value && value.length >= 8;
        break;
      default:
        isValid = value && value.trim() !== "";
    }
    if (isValid) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email || formData.email.trim() === "") {
      newErrors.email = t("validation.emailOrUsernameRequired");
    }
    if (!formData.password || formData.password.trim() === "") {
      newErrors.password = t("validation.passwordRequired");
    }
    if (!verified) {
      newErrors.recaptcha = t("validation.recaptchaRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!activeRole) return;
    const isValid = validateForm();
    if (!isValid) {
      scrollToFirstError();
      return;
    }
    try {
      setLoading(true);
      const res = await signinUser({
        ...formData,
        role: activeRole,
      });
      if (res.error) {
        dispatch(
          showSnackBar({
            text: res?.error?.response?.data?.message ?? t("common:messages.somethingWentWrong"),
            severity: "error",
          })
        );
        setLoading(false);
      } else {
        // KEEP IN SYNC: this session-writing block is mirrored by
        // persistSession() in src/helpers/session.js, which the OAuth consent
        // page (/authorize, Claude MCP) uses to sign a user in without leaving
        // that page. The two are intentionally separate copies so this
        // production login path stays untouched -- so if you add, remove,
        // rename or reorder a key below, update persistSession() as well, or
        // users who sign in through /authorize get an incomplete session.
        const rawUser = res?.data?.result?.user ?? {};
        // Backend doesn't always populate mergedPermissions (e.g. shipper helpers).
        // Derive it from roles[].permissions[] + direct permissions[] as a fallback.
        const derivedMerged = Array.from(
          new Set([
            ...(rawUser.permissions ?? []).map((p) => p?.name).filter(Boolean),
            ...(rawUser.roles ?? []).flatMap((r) =>
              (r?.permissions ?? []).map((p) => p?.name).filter(Boolean)
            ),
          ])
        );
        let user = {
          ...rawUser,
          mergedPermissions: Array.isArray(rawUser.mergedPermissions)
            ? rawUser.mergedPermissions
            : derivedMerged,
          token: res?.data?.result?.token,
        };
        localStorage.setItem("user", JSON.stringify(user));
        dispatch(addUser(user));
        localStorage.setItem("Token", res?.data?.result?.token);
        localStorage.setItem("Id", res?.data?.result?.user?.id);
        localStorage.setItem("Name", res?.data?.result?.user?.username);
        localStorage.setItem("Email", res?.data?.result?.user?.email);
        localStorage.setItem("Role", res?.data?.result?.user?.role);
        localStorage.setItem("firstname", res?.data?.result?.user?.firstname);
        localStorage.setItem("lastname", res?.data?.result?.user?.lastname);
        localStorage.setItem(
          "profile_image",
          res?.data?.result?.user?.profile_image
        );
        localStorage.setItem("Balance", res?.data?.result?.user?.balance);
        if (
          res?.data?.result?.user?.role === "Shipper" ||
          res?.data?.result?.user?.role === "ShipperUser" ||
          res?.data?.result?.user?.role === "oneTimeShipper"
        ) {
          localStorage.setItem(
            "Carriers",
            JSON.stringify(res?.data?.result?.carrier)
          );
          navigate("/");
        } else if (res?.data?.result?.user?.role === "Carrier") {
          navigate("/create-zone");
        } else if (res?.data?.result?.user?.role === "warehouseUser") {
          navigate("/warehouse-orders");
        }
        dispatch(
          showSnackBar({
            text: res?.data?.message,
            severity: "success",
          })
        );
        setLoading(false);
      }
    } catch (error) {
      dispatch(
        showSnackBar({
          text: error?.message || t("common:messages.somethingWentWrong"),
          severity: "error",
        })
      );
      setLoading(false);
    }
  };

  const onRecaptchaChange = (value) => {
    setVerified(!!value);
    if (errors.recaptcha && value) {
      setErrors((prev) => ({ ...prev, recaptcha: "" }));
    }
  };

  const scrollToFirstError = () => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return;
    const fieldRefs = {
      email: emailRef,
      password: passwordRef,
      recaptcha: recaptchaRef,
    };
    const firstErrorField = errorFields.find((field) => errors[field]);
    if (firstErrorField && fieldRefs[firstErrorField]?.current) {
      fieldRefs[firstErrorField].current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      if (fieldRefs[firstErrorField].current.focus) {
        fieldRefs[firstErrorField].current.focus();
      }
    }
  };

  // Get appropriate heading text
  const getHeadingText = () => {
    if (!activeRole) return t("login.heading.selectRole");
    if (activeRole === "warehouseUser") return t("login.heading.warehouse");
    return t(`login.heading.${role.toLowerCase()}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-light to-ui-page flex items-center justify-center py-4 px-4">
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-4">
          <div className="shipper__tab_left_logo flex justify-center">
            <img
              src={Logo}
              alt={t("login.logoAlt")}
              className="max-w-[200px] cursor-pointer"
              onClick={() => {
                window.location.href = "https://www.hajexbolt.com";
              }}
            />
          </div>
        </div>

        {/* Back Button */}
        {activeRole && (
          <div className="mb-2">
            <button
              onClick={() => navigate("/login")}
              className="inline-flex items-center gap-1 text-sm font-medium text-white bg-brand hover:bg-brand-hover px-3 py-1.5 rounded-md transition-colors duration-200"
            >
              <BackIcon />
              {t("login.back")}
            </button>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-ui-surface border border-ui-border rounded-lg overflow-hidden">
          {/* Minimal Header */}
          <div className="bg-brand px-4 py-3 text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-white/20 rounded-full mb-1">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
            </div>
            <h1 className="text-[17px] font-bold tracking-[-0.2px] text-white">{getHeadingText()}</h1>
          </div>

          {/* Content */}
          <div className="p-4">
            {!activeRole ? (
              // Role Selection with Minimal Animations
              <div className="space-y-2">
                <button
                  onClick={() => navigate("/login/shipper")}
                  className="w-full bg-brand hover:bg-brand-hover text-white text-sm font-semibold py-2.5 px-4 rounded-md transition-colors duration-150 active:scale-[0.99]"
                >
                  {t("login.roles.shipper")}
                </button>
                <button
                  onClick={() => navigate("/login/carrier")}
                  className="w-full bg-brand hover:bg-brand-hover text-white text-sm font-semibold py-2.5 px-4 rounded-md transition-colors duration-150 active:scale-[0.99]"
                >
                  {t("login.roles.carrier")}
                </button>
                <button
                  onClick={() => navigate("/login/warehouse")}
                  className="w-full bg-brand hover:bg-brand-hover text-white text-sm font-semibold py-2.5 px-4 rounded-md transition-colors duration-150 active:scale-[0.99]"
                >
                  {t("login.roles.warehouse")}
                </button>
                <div className="text-center pt-2 border-t border-ui-surface-sunken">
                  <p className="text-sm text-ui-text-muted">
                    {t("login.noAccount")}{" "}
                    <Link
                      to="/Register"
                      className="font-semibold text-brand hover:text-brand-hover transition-colors duration-200"
                    >
                      {t("login.registerHere")}
                    </Link>
                  </p>
                </div>
              </div>
            ) : (
              // Login Form
              <form onSubmit={handleLogin} className="space-y-3">
                <div className="space-y-2">
                  <InputField
                    label={t("login.usernameLabel")}
                    type="text"
                    value={formData.email}
                    ref={emailRef}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                    error={errors.email}
                    placeholder={t("login.usernamePlaceholder")}
                    icon={<EnvelopeIcon />}
                    required
                  />
                  <InputField
                    label={t("login.passwordLabel")}
                    type="password"
                    value={formData.password}
                    ref={passwordRef}
                    onChange={(e) =>
                      handleFieldChange("password", e.target.value)
                    }
                    error={errors.password}
                    placeholder={t("login.passwordPlaceholder")}
                    icon={<LockIcon />}
                    required
                  />
                </div>

                {/* reCAPTCHA - Compact */}
                <div className="flex justify-center py-1">
                  <div className="transform scale-90 origin-center">
                    <ReCAPTCHA
                      sitekey="6Ld9BMcjAAAAAE-VKFPaecDHGEqeSKQdQ_GsZ-0W"
                      onChange={onRecaptchaChange}
                      ref={recaptchaRef}
                      size="normal"
                      theme="light"
                    />
                  </div>
                </div>
                {errors.recaptcha && (
                  <p className="text-state-danger text-xs text-center">
                    {errors.recaptcha}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 px-4 rounded-md transition-colors duration-150 active:scale-[0.99]"
                >
                  {loading ? <BtnLoader /> : t("login.signIn")}
                </button>

                {/* Links */}
                <div className="text-center space-y-1 pt-2 border-t border-ui-surface-sunken">
                  <Link
                    to="/get-code"
                    className="block text-sm text-brand hover:text-brand-hover font-medium transition-colors duration-200"
                  >
                    {t("login.forgotPassword")}
                  </Link>
                  <p className="text-sm text-ui-text-muted">
                    {t("login.noAccount")}{" "}
                    <Link
                      to="/Register"
                      className="font-semibold text-brand hover:text-brand-hover transition-colors duration-200"
                    >
                      {t("login.registerHere")}
                    </Link>
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-ui-text-muted mt-3">
          {t("login.needHelp")}{" "}
          <a
            href="mailto:info@hajexbolt.com"
            className="text-brand hover:text-brand-hover font-medium transition-colors duration-200"
          >
            {t("login.contactSupport")}
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
